<script setup lang="ts">
import {
    BAvatar, BMenu, BIcon, BListItem, BListItemTitle, BListItemSubtitle, BDivider,
} from '@/design-system';
import { useTranslation } from '@/composables';
import { useAuthStore } from '@/stores/authStore';
import { SharedRoutes } from '@/types/enums/RouteNameEnums';
import router from '@/plugins/router';

type PropsType = {
    userAvatar?: string;
    avatarSize?: string | number;
    avatarClass?: string;
    listDensity?: 'default' | 'compact' | 'comfortable';
    listClass?: string;
};

withDefaults(defineProps<PropsType>(), {
    avatarSize: 32,
    listDensity: 'default',
});

const { t } = useTranslation();
const authStore = useAuthStore();

const logout = async () => {
    await authStore.logout();
};

defineOptions({ inheritAttrs: false });
</script>

<template>
    <BMenu
        :list-density="listDensity"
        v-bind="$attrs"
        :list-classes="listClass"
    >
        <template #activator="{ props }">
            <BAvatar
                :size="avatarSize"
                :image="userAvatar"
                :text="userAvatar ? undefined : authStore.userInitials"
                :class="avatarClass"
                v-bind="props"
                color="primary"
                text-color="white"
                clickable
            />
        </template>

        <template #list-items>
            <BListItem class="pb-3">
                <BListItemTitle class="text-grey-700"
                >{{ authStore.user?.first_name }} {{ authStore.user?.last_name }}</BListItemTitle
                >
                <BListItemSubtitle>{{ authStore.user?.email }}</BListItemSubtitle>
            </BListItem>

            <BDivider />

            <BListItem
                density="compact"
                @click="router.push({ name: SharedRoutes.Profile })"
            >
                <BListItemTitle class="d-flex align-center">
                    <BIcon
                        icon="mdi-account-outline"
                        class="mr-3 opacity-60"
                        size="small"
                        color="grey-800"
                    />
                    <span class="b-list-item-text text-grey-700">{{ t('layout.profile') }}</span>
                </BListItemTitle>
            </BListItem>

            <BListItem
                v-if="!authStore.isImpersonating"
                @click="logout"
                density="compact"
            >
                <BListItemTitle class="d-flex align-center">
                    <BIcon
                        icon="mdi-logout"
                        class="mr-3 opacity-60"
                        size="small"
                        color="grey-800"
                    />
                    <span class="b-list-item-text text-grey-700">{{ t('layout.logout') }}</span>
                </BListItemTitle>
            </BListItem>
        </template>
    </BMenu>
</template>

<style scoped lang="scss">
.b-list-item-text {
    font-size: 0.9rem;
}
:deep(.v-list-item-title) {
    letter-spacing: normal;
}
</style>
